import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";

const PER_PAGE = 10;
const LOAD_THRESHOLD = 1400;
const PRIORITY_CARD_COUNT = 2;
const STORY_CACHE_KEY = "trw-feature-grid-stories";
const STORY_CACHE_VERSION = "v3";
const STORY_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const STORY_FEED_PATH = "/wp-json/web-stories/v1/web-story";
const STORY_FEED_ORIGIN = "https://travel.rethinkways.com";
const STORY_FIELDS = "id,link,title,story_poster,tags,categories,web_story_tag,web-story-tag";
const STORY_TERM_FIELDS = "id,name,slug,count";
const ALL_STORIES_TAG = "All";
const STORY_TAGS = [
  ALL_STORIES_TAG,
  "Must-Visit Places",
  "Unique Stays",
  "Food & Culture",
  "Romantic Escapes",
  "Adventure",
  "Seasonal Picks"
];
const STORY_TERM_ENDPOINTS = [
  "/wp-json/web-stories/v1/web_story_tag"
];
const STORY_FILTER_KEY = "web_story_tag";
const STORY_TAG_FILTERS = {
  "Must-Visit Places": [5065],
  "Unique Stays": [5071, 1191, 1189, 1211, 1184, 1213, 1180, 1198, 1202],
  "Food & Culture": [5080, 5085, 5086, 5090, 956, 959],
  "Romantic Escapes": [5107, 5119, 1214, 1185, 950],
  "Adventure": [5106, 5104],
  "Seasonal Picks": [5095, 5099]
};
const STORY_TITLE_REPLACEMENTS = new Map([
  ["Stay in Style: Tamil Nadu’s Best Boutique Hotels", "Stay in Style: Tamil Nadu’s Boutique Hotels"],
  ["Top Winter Getaways in India You Can’t Miss", "Top Winter Getaways in India Can’t Miss"],
  ["India’s Best January Destinations to Explore", "India’s January Destinations to Explore"],
  ["Untold Karnataka: Where Secrets Await", "Karanatakas's Hidden Gems The untold side"],
  ["India’s Top Safari Escapes You Must Experience", "India's Most Thrilling Safaris Experience"],
  ["8 Iconic Landmarks in Delhi You Must Visit", "8 Must-Visit landmarks in delhi You II love"],
  ["India’s Most Romantic Candlelight Experiences", "India's Ultimate candle light Dinning Escape"],
  ["India’s Most Elegant Honeymoon Destinations<", "India’s Elegant Honeymoon Destinations<"],
  ["India’s Most Elegant Honeymoon Destinations", "India’s Elegant Honeymoon Destinations"]
]);
function decodeHtml(value) {
  if (typeof window === "undefined") return value;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

function getStoryImage(post) {
  const poster = post?.story_poster;
  if (!poster) return null;

  const src = poster?.url || poster?.src || null;
  const width = Number(poster?.width);
  const height = Number(poster?.height);

  let srcSet = "";
  const sizes = Array.isArray(poster?.sizes) ? poster.sizes : [];
  if (sizes.length) {
    const entries = sizes
      .map((item) => {
        const itemUrl = item?.url || item?.src || item?.source_url;
        const itemWidth = Number(item?.width);
        if (!itemUrl || !Number.isFinite(itemWidth)) return null;
        return `${itemUrl} ${itemWidth}w`;
      })
      .filter(Boolean);

    srcSet = entries.join(", ");
  }

  return { src, srcSet, width: Number.isFinite(width) ? width : null, height: Number.isFinite(height) ? height : null };
}

function formatStoryTitle(title) {
  const replacementTitle = STORY_TITLE_REPLACEMENTS.get(title);
  if (replacementTitle) return replacementTitle;

  return title.replace(/\s+to\s+visit\s*$/i, "");
}

function normalizeTerm(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&amp;/g, "&")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildQueryString(params) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    searchParams.set(key, value);
  });

  return searchParams.toString();
}

function getRequestUrls(path) {
  return [`/api/jnews${path}`, `${STORY_FEED_ORIGIN}${path}`];
}

function getStoryTermIds(post) {
  return [
    post?.tags,
    post?.categories,
    post?.web_story_tag,
    post?.["web-story-tag"]
  ]
    .filter(Array.isArray)
    .flat()
    .map((id) => Number(id))
    .filter(Number.isFinite);
}

function resolveStoryTerms(label, terms) {
  if (label === ALL_STORIES_TAG) return [];

  const configuredIds = STORY_TAG_FILTERS[label] || [];
  if (configuredIds.length > 0) {
    return configuredIds.map((id) => ({ id, name: label, slug: normalizeTerm(label) }));
  }

  const normalizedLabel = normalizeTerm(label);
  const matchedTerms = terms.filter(
    (term) => normalizeTerm(term.name) === normalizedLabel || normalizeTerm(term.slug) === normalizedLabel
  );

  return matchedTerms.length > 0
    ? matchedTerms
    : [{ id: null, name: label, slug: normalizedLabel }];
}

function postMatchesStoryTerms(post, terms) {
  const termIdsToMatch = terms.map((term) => Number(term.id)).filter(Number.isFinite);
  if (termIdsToMatch.length === 0) return true;

  const termIds = getStoryTermIds(post);
  return termIds.some((termId) => termIdsToMatch.includes(termId));
}

function mapPostToCard(post, index) {
  const title = decodeHtml(post.title?.rendered || `Travel story ${index + 1}`);
  const image = getStoryImage(post);
  const displayTitle = formatStoryTitle(title);
  return {
    id: `jnews-${post.id}`,
    src: image?.src || null,
    srcSet: image?.srcSet || "",
    width: image?.width || null,
    height: image?.height || null,
    title: displayTitle,
    alt: title,
    link: post.link,
  };
}

function dedupeCards(cards) {
  const seen = new Set();

  return cards.filter((card) => {
    const key = `${card.id}::${card.link}::${card.title}`.toLowerCase();
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function isFallbackCard(card) {
  return String(card?.id || "").startsWith("fallback-");
}

function hasOnlyFallbackCards(cards) {
  return Array.isArray(cards) && cards.length > 0 && cards.every(isFallbackCard);
}

function getRealStoryCards(cards) {
  if (!Array.isArray(cards)) {
    return [];
  }

  return cards.filter((card) => card?.src && !isFallbackCard(card));
}

function getStoryCacheKey(tagLabel) {
  return `${STORY_CACHE_KEY}:${normalizeTerm(tagLabel || ALL_STORIES_TAG)}`;
}

function getMinimumCachedCards(tagLabel = ALL_STORIES_TAG) {
  return tagLabel === ALL_STORIES_TAG ? PER_PAGE : 1;
}

function readStoryCache(tagLabel = ALL_STORIES_TAG) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const cacheKey = getStoryCacheKey(tagLabel);
    const rawCache = window.localStorage.getItem(cacheKey);
    if (!rawCache) return null;

    const parsedCache = JSON.parse(rawCache);

    if (Array.isArray(parsedCache)) {
      const realCards = getRealStoryCards(parsedCache);
      if (realCards.length < getMinimumCachedCards(tagLabel)) {
        window.localStorage.removeItem(cacheKey);
        return null;
      }

      return realCards;
    }

    if (!parsedCache || typeof parsedCache !== "object") {
      return null;
    }

    const { version, timestamp, cards } = parsedCache;
    const realCards = getRealStoryCards(cards);
    const isVersionMatch = version === STORY_CACHE_VERSION;
    const isFresh = Number.isFinite(timestamp) && Date.now() - timestamp < STORY_CACHE_TTL_MS;

    if (
      !isVersionMatch ||
      !isFresh ||
      realCards.length < getMinimumCachedCards(tagLabel) ||
      hasOnlyFallbackCards(cards)
    ) {
      window.localStorage.removeItem(cacheKey);
      return null;
    }

    return realCards;
  } catch {
    window.localStorage.removeItem(getStoryCacheKey(tagLabel));
    return null;
  }
}

function writeStoryCache(cards, tagLabel = ALL_STORIES_TAG) {
  if (typeof window === "undefined") {
    return;
  }

  const realCards = getRealStoryCards(cards);

  try {
    const cacheKey = getStoryCacheKey(tagLabel);

    if (realCards.length < getMinimumCachedCards(tagLabel)) {
      window.localStorage.removeItem(cacheKey);
      return;
    }

    window.localStorage.setItem(
      cacheKey,
      JSON.stringify({
        version: STORY_CACHE_VERSION,
        timestamp: Date.now(),
        cards: realCards
      })
    );
  } catch {
    // Ignore localStorage write errors.
  }
}

function getInitialCards(tagLabel = ALL_STORIES_TAG) {
  if (typeof window === "undefined") {
    return [];
  }

  return readStoryCache(tagLabel) ?? [];
}

function mergeStoryCards(currentCards, nextCards) {
  if (hasOnlyFallbackCards(currentCards)) {
    return dedupeCards(nextCards);
  }

  return dedupeCards([...currentCards.filter((card) => !isFallbackCard(card)), ...nextCards]);
}

async function fetchJsonPath(path) {
  let lastError = null;

  for (const requestUrl of getRequestUrls(path)) {
    try {
      const response = await fetch(requestUrl, { cache: "force-cache" });
      if (!response.ok) throw new Error(`Failed to fetch WordPress data: ${response.status}`);
      return response.json();
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to fetch WordPress data.");
}

async function fetchJsonPathWithHeaders(path) {
  let lastError = null;

  for (const requestUrl of getRequestUrls(path)) {
    try {
      const response = await fetch(requestUrl, { cache: "force-cache" });
      if (!response.ok) throw new Error(`Failed to fetch WordPress data: ${response.status}`);
      const data = await response.json();
      return { data, headers: response.headers };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to fetch WordPress data.");
}

async function fetchWordPressTerms() {
  const termGroups = await Promise.all(
    STORY_TERM_ENDPOINTS.map(async (endpoint) => {
      try {
        const query = buildQueryString({
          per_page: 100,
          _fields: STORY_TERM_FIELDS
        });
        const terms = await fetchJsonPath(`${endpoint}?${query}`);
        return Array.isArray(terms) ? terms : [];
      } catch {
        return [];
      }
    })
  );

  const termsBySlug = new Map();

  termGroups.flat().forEach((term) => {
    if (!term?.name || !term?.slug) return;
    termsBySlug.set(normalizeTerm(term.slug), term);
  });

  return Array.from(termsBySlug.values());
}

function buildStoryFeedPaths(page, activeTag, terms) {
  const baseParams = {
    per_page: PER_PAGE,
    page,
    orderby: "date",
    order: "desc",
    _fields: STORY_FIELDS
  };
  const resolvedTerms = resolveStoryTerms(activeTag, terms);

  if (activeTag === ALL_STORIES_TAG) {
    return [`${STORY_FEED_PATH}?${buildQueryString(baseParams)}`];
  }

  const filterValue = resolvedTerms
    .map((term) => term.id || term.slug)
    .filter(Boolean)
    .join(",");

  return [`${STORY_FEED_PATH}?${buildQueryString({ ...baseParams, [STORY_FILTER_KEY]: filterValue })}`];
}

async function fetchStoryPage(page, activeTag, terms) {
  const feedPaths = buildStoryFeedPaths(page, activeTag, terms);
  let lastError = null;

  for (const feedPath of feedPaths) {
    try {
      const { data, headers } = await fetchJsonPathWithHeaders(feedPath);
      const resolvedTerms = resolveStoryTerms(activeTag, terms);
      const postList = Array.isArray(data) ? data : [];
      const matchingPosts =
        activeTag === ALL_STORIES_TAG
          ? postList
          : postList.filter((post) => postMatchesStoryTerms(post, resolvedTerms));

      const totalPagesRaw = headers?.get?.("x-wp-totalpages") ?? headers?.get?.("X-WP-TotalPages");
      const totalPages = totalPagesRaw ? Number(totalPagesRaw) : null;

      return {
        cards: dedupeCards(matchingPosts.map(mapPostToCard).filter((card) => Boolean(card.src))),
        totalPages: Number.isFinite(totalPages) ? totalPages : null,
      };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to fetch JNews stories.");
}

function warmStoryImages(cards) {
  if (typeof window === "undefined") {
    return;
  }

  cards.slice(0, PRIORITY_CARD_COUNT).forEach((card) => {
    if (!card?.src) return;
    const image = new Image();
    image.decoding = "async";
    image.src = card.src;
  });
}

async function prefetchStoryTags(terms, cardsByTag) {
  await Promise.all(
    STORY_TAGS.map(async (tag) => {
      const cachedCards = readStoryCache(tag);
      if (cachedCards) {
        cardsByTag.set(tag, cachedCards);
        warmStoryImages(cachedCards);
        return;
      }

      try {
        // Skip eager prefetching over the network; rely on cache + on-demand loads.
        // This keeps initial WebStory load fast on mobile.
      } catch {
        // Keep tag switching usable even if one background request fails.
      }
    })
  );
}

function StoryCard({
  card,
  className = "",
  bottomOnlyRadius = false,
  isPriority = false
}) {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const radiusClass = bottomOnlyRadius
    ? "rounded-t-none rounded-b-[15px] sm:rounded-b-[28px]"
    : "rounded-[15px] sm:rounded-[28px]";

  return (
    <article
      className={`transition-transform duration-300 ${className}`}
      style={isPriority ? undefined : { contentVisibility: "auto", containIntrinsicSize: "560px 880px" }}
    >
      <a
        href={card.link}
        target="_blank"
        rel="noreferrer"
        className={`group relative block overflow-hidden bg-white transition duration-300 ${radiusClass}`}
      >
        <div className="relative w-full overflow-hidden aspect-[9/14] max-h-[412px] sm:max-h-none">
          <img
            src={card.src}
            alt={card.alt}
            width={card.width || 900}
            height={card.height || 1400}
            srcSet={card.srcSet || undefined}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 20vw"
            onLoad={() => setIsImageLoaded(true)}
            loading={isPriority ? "eager" : "lazy"}
            fetchPriority={isPriority ? "high" : "low"}
            decoding="async"
            className="h-full w-full object-cover transition duration-500"
          />

          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-[1]"
            style={{
              height: "46%",
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(26,26,26,0.45) 55%, rgba(51,51,51,0.92) 100%)"
            }}
          />

          {isImageLoaded ? (
            <>
              <span className="absolute inset-0 z-[2] flex items-center justify-center sm:hidden" aria-hidden="true">
                <span
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 207'%3E%3Ccircle fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-width='4.6' stroke-dasharray='420 16 36 16 36 22' stroke-dashoffset='178' cx='100' cy='100' r='87'/%3E%3Cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='4.6' d='M84 72v56l44-28-44-28Z'/%3E%3C/svg%3E\")",
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    width: "56px",
                    height: "56px",
                  }}
                />
              </span>

              <span
                className="absolute left-[15px] sm:left-[29px] top-0 z-[2] inline-flex w-fit min-h-[28px] items-center justify-center bg-[#d02525] px-3 text-[14px] font-semibold leading-[12px] text-white uppercase"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  top: 0
                }}
              >
                WEB STORY
              </span>

              <span
                className="absolute z-[2] rounded-full"
                style={{
                  top: "12px",
                  right: "12px",
                  width: "32px",
                  height: "32px",
                  background: "url(https://static.toiimg.com/photo/76150242.cms) center center no-repeat rgb(26 26 26 / 0.9)",
                  backgroundSize: "12px"
                }}
                aria-hidden="true"
              />

              <div className="absolute bottom-0 left-0 z-[2] flex w-full flex-col items-center sm:items-end sm:w-[min(100%,425px)] lg:w-full">
                <div
                  className="px-3 pt-3 pb-3 text-center sm:px-6 sm:pt-5 sm:pb-0 sm:text-left"
                  style={{ fontFamily: "'Poppins', sans-serif", marginTop: "1rem" }}
                >
                  <h2
                    className="overflow-hidden text-center text-[20px] tracking-[1px] leading-[24px] font-normal text-white [font-family:Poppins,sans-serif] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:4] sm:text-left sm:block sm:overflow-visible sm:text-[30px] sm:leading-[38px] sm:tracking-[0px] sm:[font-family:'Yeseva_One',Georgia,serif] sm:[-webkit-line-clamp:unset] lg:mb-[10px] lg:text-[26px] lg:leading-[32px]"
                  >
                    {card.title}
                  </h2>
                </div>

                <div className="relative hidden min-h-[58px] w-full justify-center pt-1 sm:flex sm:min-h-[70px] sm:pt-2 lg:min-h-[70px]">
                  <span className="relative inline-flex w-full items-center justify-center border-0 bg-transparent transition-transform duration-[160ms] ease-[ease]">
                    <span
                      aria-hidden="true"
                      className="absolute hidden sm:block"
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 207'%3E%3Ccircle fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-width='4.6' stroke-dasharray='420 16 36 16 36 22' stroke-dashoffset='178' cx='100' cy='100' r='87'/%3E%3Cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='4.6' d='M84 72v56l44-28-44-28Z'/%3E%3C/svg%3E\")",
                        backgroundSize: "100% 100%",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        bottom: "12px",
                        right: "12px",
                        width: "48px",
                        height: "48px"
                      }}
                    />
                  </span>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </a>
    </article>
  );
}

export default function WebStory() {
  // State for filter tags and stories
  const [activeTag, setActiveTag] = useState(ALL_STORIES_TAG);
  const [cards, setCards] = useState(() => getInitialCards(ALL_STORIES_TAG));
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState("");
  const fetchedPagesRef = useRef(new Set());
  const isFetchingRef = useRef(false);
  const requestIdRef = useRef(0);
  const termsRef = useRef([]);
  const totalPagesRef = useRef(new Map());
  const cardsByTagRef = useRef(
    new Map(STORY_TAGS.map((tag) => [tag, getInitialCards(tag)]).filter(([, cachedCards]) => cachedCards.length > 0))
  );

  const showTag = useCallback((tag) => {
    const cachedCards = cardsByTagRef.current.get(tag) || readStoryCache(tag);
    if (cachedCards?.length) {
      cardsByTagRef.current.set(tag, cachedCards);
      setCards(cachedCards);
      warmStoryImages(cachedCards);
    } else {
      setCards([]);
    }

    setActiveTag(tag);
  }, []);

  // Always render filter tags immediately
  const filterTags = (
    <div
      className="mb-8 flex flex-wrap items-center justify-center gap-3 text-center"
      style={{ fontFamily: "Poppins, sans-serif" }}
      aria-label="Story categories"
    >
      {STORY_TAGS.map((tag) => (
        <button
          key={tag}
          type="button"
          onClick={() => showTag(tag)}
          onMouseEnter={() => {
            const cachedCards = cardsByTagRef.current.get(tag) || readStoryCache(tag);
            if (cachedCards?.length) {
              cardsByTagRef.current.set(tag, cachedCards);
              warmStoryImages(cachedCards);
            }
          }}
          aria-pressed={activeTag === tag}
          className={`rounded-[12px] border px-[10px] py-[5px] text-[14px] font-[400] leading-[20px] transition-colors sm:px-4 sm:py-[11px] ${
            activeTag === tag
              ? "border-[#d02525] bg-[#d02525] text-white"
              : "border-black text-black hover:border-[#ff1f1f] hover:text-[#ff1f1f] [.trw-dark-mode_&]:border-white [.trw-dark-mode_&]:text-white [.trw-dark-mode_&]:hover:border-[#f87171] [.trw-dark-mode_&]:hover:text-[#f87171]"
          }`}
        >
          {tag}
        </button>
      ))}
    </div>
  );

  const storyColumns = useMemo(
    () =>
      Array.from({ length: 5 }, (_, colIdx) =>
        cards
          .map((card, index) => ({ card, index }))
          .filter((_, i) => i % 5 === colIdx)
      ),
    [cards]
  );

  const lcpImageHref = useMemo(() => {
    const firstCardWithImage = Array.isArray(cards) ? cards.find((card) => Boolean(card?.src)) : null;
    return firstCardWithImage?.src || "";
  }, [cards]);

  const tabletStoryColumns = useMemo(() => {
    const fifthColumn = storyColumns[4] || [];
    const splitIndex = Math.ceil(fifthColumn.length / 2);

    return [
      ...storyColumns.slice(0, 4),
      fifthColumn.slice(0, splitIndex),
      fifthColumn.slice(splitIndex)
    ];
  }, [storyColumns]);
  const desktopStoryColumns = useMemo(() => {
    if (activeTag !== ALL_STORIES_TAG) {
      return storyColumns;
    }

    const columns = storyColumns.map((columnItems) => [...columnItems]);
    const firstColumnLastItem = columns[0]?.pop();

    if (firstColumnLastItem) {
      columns[3].push(firstColumnLastItem);
    }

    return columns;
  }, [activeTag, storyColumns]);

  const columnOffsetClasses = [
    "pt-0 -mt-[138px] md:-mt-[220px] lg:mt-0",
    "pb-0 lg:-mt-20",
    "pt-0 -mt-[138px] md:-mt-[220px] lg:mt-0",
    "pb-0 lg:-mt-20",
    "pt-0 -mt-[138px] md:-mt-[220px] lg:mt-0",
    "flex pt-0 lg:hidden"
  ];
  const loadStoryPage = useCallback(async (nextPage, replaceCards = false) => {
    if (isFetchingRef.current || fetchedPagesRef.current.has(nextPage)) return;

    const requestId = ++requestIdRef.current;
    isFetchingRef.current = true;
    setIsLoadingMore(true);
    setError("");

    try {
      const { cards: nextCards, totalPages } = await fetchStoryPage(nextPage, activeTag, termsRef.current);
      if (requestId !== requestIdRef.current) return;

      fetchedPagesRef.current.add(nextPage);
      if (totalPages) {
        totalPagesRef.current.set(activeTag, totalPages);
      }
      setCards((cur) => {
        const merged = replaceCards ? dedupeCards(nextCards) : mergeStoryCards(cur, nextCards);
        cardsByTagRef.current.set(activeTag, merged);
        writeStoryCache(merged, activeTag);
        return merged;
      });
      warmStoryImages(nextCards);
      setPage((cur) => (replaceCards ? nextPage + 1 : Math.max(cur, nextPage + 1)));

      const knownTotalPages = totalPagesRef.current.get(activeTag);
      if (knownTotalPages) {
        setHasMore(nextPage < knownTotalPages);
      } else {
        // Fallback when headers are unavailable.
        setHasMore(nextCards.length === PER_PAGE);
      }
    } catch {
      if (requestId !== requestIdRef.current) return;
      setError(`Unable to load ${activeTag === ALL_STORIES_TAG ? "more JNews" : activeTag} travel stories.`);
      setHasMore(false);
    } finally {
      if (requestId === requestIdRef.current) {
        isFetchingRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [activeTag]);

  const loadAllStoryPages = useCallback(
    async (tagLabel) => {
      if (isFetchingRef.current) return;

      const requestId = ++requestIdRef.current;
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      setError("");

      try {
        // Always start with page 1 to discover total pages from headers.
        const first = await fetchStoryPage(1, tagLabel, termsRef.current);
        if (requestId !== requestIdRef.current) return;

        const totalPages = first.totalPages ?? 1;
        totalPagesRef.current.set(tagLabel, totalPages);

        let allCards = first.cards;

        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, idx) =>
              fetchStoryPage(idx + 2, tagLabel, termsRef.current).catch(() => ({ cards: [], totalPages: null }))
            )
          );
          if (requestId !== requestIdRef.current) return;

          rest.forEach((pageResult) => {
            allCards = dedupeCards([...allCards, ...pageResult.cards]);
          });
        }

        fetchedPagesRef.current = new Set(Array.from({ length: totalPages }, (_, idx) => idx + 1));

        setCards(() => {
          const merged = dedupeCards(allCards);
          cardsByTagRef.current.set(tagLabel, merged);
          writeStoryCache(merged, tagLabel);
          return merged;
        });
        warmStoryImages(allCards);

        setPage(totalPages + 1);
        setHasMore(false);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setError(`Unable to load ${tagLabel === ALL_STORIES_TAG ? "JNews" : tagLabel} travel stories.`);
        setHasMore(false);
      } finally {
        if (requestId === requestIdRef.current) {
          isFetchingRef.current = false;
          setIsLoadingMore(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    let isMounted = true;
    // Fetch terms in background, but filter tags are always rendered
    fetchWordPressTerms().then((nextTerms) => {
      if (isMounted) termsRef.current = nextTerms;
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    requestIdRef.current += 1;
    fetchedPagesRef.current.clear();
    isFetchingRef.current = false;
    setPage(1);
    setHasMore(true);
    setError("");
    setCards(cardsByTagRef.current.get(activeTag) || getInitialCards(activeTag));
    loadAllStoryPages(activeTag);
  }, [activeTag, loadAllStoryPages]);

  useEffect(() => {
    const handleScroll = () => {
      if (isLoadingMore || !hasMore) return;
      const scrollPosition = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.scrollHeight;
      if (scrollPosition < pageHeight - LOAD_THRESHOLD) return;
      loadStoryPage(page);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, isLoadingMore, loadStoryPage, page]);

  return (
    <section className="trw-webstory-page bg-white">
      {lcpImageHref ? (
        <Helmet>
          <link rel="preload" as="image" href={lcpImageHref} fetchPriority="high" />
        </Helmet>
      ) : null}
      <div className="mx-auto mt-[35px] max-w-[1500px] px-[8px] sm:px-[16px]">
	        <h1
	          className="mb-8 text-center text-black text-[24px] [.trw-dark-mode_&]:text-white leading-[32px] sm:text-[36px] sm:leading-[36px]"
	          style={{
	            fontFamily: 'Poppins, sans-serif',
	            fontStyle: 'normal',
	            fontWeight: 500
	          }}
	        >
         Story-Driven Travel Inspiration
        </h1>

        {filterTags}

        {error ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-x-2 gap-y-5 overflow-hidden sm:gap-x-4 lg:hidden">
          {tabletStoryColumns.map((columnItems, columnIndex) => (
            <div
              key={`tablet-column-${columnIndex}`}
              className={`flex min-w-0 flex-col gap-5 ${columnOffsetClasses[columnIndex]}`}
            >
              {columnItems.map(({ card, index }, rowIndex) => (
                <StoryCard
                  key={card.id}
                  card={card}
                  bottomOnlyRadius={false}
                  isPriority={rowIndex === 0}
                  className={`${columnIndex === 5 && rowIndex === 0 ? "mt-[7px]" : ""} ${
                    columnIndex % 2 === 1 && rowIndex === 0 ? "lg:-mt-[50%]" : ""
                  }`}
                />
              ))}
            </div>
          ))}
        </div>

        <div className="hidden gap-x-4 gap-y-5 overflow-hidden lg:grid lg:grid-cols-5">
          {desktopStoryColumns.map((columnItems, columnIndex) => (
            <div
              key={`desktop-column-${columnIndex}`}
              className={`flex flex-col gap-5 ${columnOffsetClasses[columnIndex]}`}
            >
              {columnItems.map(({ card, index }, rowIndex) => (
                <StoryCard
                  key={card.id}
                  card={card}
                  bottomOnlyRadius={false}
                  isPriority={rowIndex === 0}
                  className={columnIndex % 2 === 1 && rowIndex === 0 ? "lg:-mt-[50%]" : ""}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
